import React from 'react';
import { Universe } from '../../Types/Universe';
import { Image as ImageType } from '../../Types/Image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPencil } from '@fortawesome/free-solid-svg-icons';
import Image from '../../Components/Image';

interface UniverseCardProps {
  title: string;
  description: string;
  image: ImageType;
  isSelected: boolean;
  isActive: boolean;
  isEnabled: boolean;
  universe: Universe;
  cssHeight?: string;
  onClick: (universe: Universe) => void;
  onEdit?: (universe: Universe) => void;
}

const UniverseCard: React.FC<UniverseCardProps> = ({
  title,
  description,
  image,
  isSelected = false,
  isActive,
  isEnabled,
  universe,
  cssHeight,
  onClick,
  onEdit,
}) => {
  return (
    <div
      data-testid={`universe-selection-card-${title}`}
      className="
          flex flex-col justify-center w-full text-sm rounded-xl max-md:mt-3 cursor-pointer bg-gray-50 border border-gray-200
          max-lg:mt-0
          sm:grow
        "
    >
      <div
        className={`
              flex overflow-hidden relative flex-col w-full ${cssHeight ?? 'h-[180px]'}
            `}
        onClick={() => isEnabled && onClick(universe)}
      >
        {image && (
          <div className={`absolute inset-0 bg-gray-100 rounded-xl`}>
            <Image
              src={[
                image.thumbnailUrl,
                image.compressedUrl,
                image.originalUrl,
                image.sharedStorageUrl,
              ]}
              loader={
                <div className="h-full w-full animate-shimmer rounded-xl" />
              }
              retries={60 * 60 * 24}
              alt={title}
              className={`object-cover absolute inset-0 rounded-xl size-full`}
            />
          </div>
        )}
        {!image && (
          <div
            className={`flex absolute px-2 py-2 tracking-wide text-start text-gray-500 text-ellipsis italic`}
          >
            <div>Hang tight! Image is coming soon...</div>
          </div>
        )}
        {isSelected && (
          <>
            <div className="absolute right-2.5 top-2.5 bg-blue-600 rounded-full w-10 h-10 flex justify-center items-center">
              <FontAwesomeIcon icon={faCheck} color={'white'} />
            </div>
            <div
              className={`absolute top-0 bottom-0 left-0 right-0 rounded-xl z-[1] ${isSelected ? 'shadow-[0_0_0_8px_rgba(37,99,235)_inset]' : 'border border-gray-200'}`}
            ></div>
          </>
        )}
        <div
          className={`absolute flex flex-col top-0 left-0 w-full h-full ${
            !image
              ? 'text-gray-800'
              : 'bg-gradient-to-t from-black via-black-500/25 to-transparent text-white'
          } rounded-xl`}
          style={{
            backgroundSize: '100% 80%',
            backgroundPosition: 'bottom',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className={`px-3 pb-2 mt-auto`}>
            <div
              className={`mb-auto text-left text-base font-medium max-md:mt-5 max-md:mr-2.5`}
            >
              {title}
              {universe.createdByUserId && (
                <>
                  <div className="justify-center self-end px-2.5 py-1.5 text-sm tracking-wide text-center text-white bg-blue-600 rounded-br-xl rounded-tl-xl shadow-sm absolute top-0 left-0 z-10">
                    Created By Me
                  </div>
                  {onEdit && (
                    <div
                      className={`flex justify-center items-center w-10 h-10 text-center text-white bg-blue-600 rounded-full shadow-sm absolute top-2.5 ${isSelected ? 'right-14' : 'right-2.5'} z-10 cursor-pointer hover:bg-blue-700`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onEdit(universe);
                      }}
                    >
                      <FontAwesomeIcon icon={faPencil} />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniverseCard;
